import React, { useEffect } from 'react';

import { Checkbox, FormControlLabel, Paper, Typography } from '@material-ui/core';
import type { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import FormRichTextInput from 'components/FormRichTextInput/FormRichTextInput';
import FormSelect from 'components/FormSelect/FormSelect';
import FormTagsPicker from 'components/FormTagsPicker/FormTagsPicker';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import type { AreasResponse } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';
import type { Option } from 'types/Option';

import type { ActionTemplateFormValues } from '../../ActionTemplateForm.types';

import useStyles from './ActionTemplateProperties.styles';

type ActionTemplatePropertiesProps = {
  formik: FormikProps<ActionTemplateFormValues>;
  choicesData?: {
    data: {
      purpose: Array<{ value: string; label: string }>;
      criticality: Array<{ value: string; label: string }>;
      area: Array<{ value: string; label: string }>;
    };
  };
  areasData?: {
    data: AreasResponse;
  };
  assetTypes?: Option[];
};

const ActionTemplateProperties: React.FC<ActionTemplatePropertiesProps> = ({ formik, choicesData, areasData, assetTypes }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = formik;
  const styles = useStyles();

  const availableSubareas = React.useMemo(() => {
    if (!values.area || !areasData?.data?.areas) return [];

    // Convert values.area to number for comparison because select value might be string
    const selectedArea = areasData.data.areas.find(area => String(area.value) === String(values.area));

    const subareas =
      selectedArea?.subareas?.map(subarea => ({
        key: subarea.value,
        label: subarea.label,
      })) || [];

    return subareas.sort((a, b) => a.label.localeCompare(b.label));
  }, [values.area, areasData]);

  useEffect(() => {
    // Only run this effect if areasData is available (loaded)
    if (!areasData?.data?.areas) {
      return;
    }

    if (values.subarea && !availableSubareas.find(sub => sub.key === values.subarea)) {
      setFieldValue('subarea', '');
    }
  }, [values.area, availableSubareas, values.subarea, setFieldValue, areasData]);

  useEffect(() => {
    if (values.purpose !== 'assets' && values.assetTypes.length > 0) {
      setFieldValue('assetTypes', []);
    }
  }, [values.purpose, values.assetTypes, setFieldValue]);

  // Synchronize checkbox when validityPeriod is "0"
  useEffect(() => {
    // If validityPeriod is "0", check the checkbox (only if not already checked)
    if (values.validityPeriod === '0' && !values.validityPeriodDoesNotApply) {
      setFieldValue('validityPeriodDoesNotApply', true);
    }
    // If validityPeriod is not "0" and not empty, uncheck the checkbox (only if currently checked)
    else if (values.validityPeriod !== '' && values.validityPeriod !== '0' && values.validityPeriodDoesNotApply) {
      setFieldValue('validityPeriodDoesNotApply', false);
    }
  }, [values.validityPeriod, values.validityPeriodDoesNotApply, setFieldValue]);

  // Custom handler for checkbox
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFieldValue('validityPeriodDoesNotApply', checked);

    // If checkbox is checked, set validityPeriod to "0"
    // If checkbox is unchecked, clear validityPeriod
    if (checked) {
      setFieldValue('validityPeriod', '0');
    } else {
      setFieldValue('validityPeriod', '');
    }
  };

  // Sort criticality options: high -> medium -> low
  const getCriticalitySortOrder = (value: string): number => {
    switch (value) {
      case 'high':
        return 1;
      case 'medium':
        return 2;
      case 'low':
        return 3;
      default:
        return 999;
    }
  };

  const sortedCriticalityOptions = React.useMemo(() => {
    if (!choicesData?.data.criticality) return [];
    return [...choicesData.data.criticality]
      .map(choice => ({
        key: choice.value,
        label: choice.label,
      }))
      .sort((a, b) => getCriticalitySortOrder(a.key) - getCriticalitySortOrder(b.key));
  }, [choicesData?.data.criticality]);

  return (
    <Paper style={{ padding: 16, marginBottom: 16 }}>
      <Typography style={{ marginBottom: 16 }} variant='h6'>
        {t(action_library_messages.properties.section_title)}
      </Typography>

      <CenteredGrid>
        <FormTextInput className={styles.marginBottom} formik={formik} id='title' label={t(action_library_messages.properties.title)} />
        <FormRichTextInput
          className={styles.richTextMarginBottom}
          formik={formik}
          id='shortDescription'
          label={t(action_library_messages.properties.short_description)}
          required
        />
        <FormSelect
          className={styles.marginBottom}
          disabled
          formik={formik}
          id='purpose'
          label={t(action_library_messages.properties.purpose)}
          options={
            choicesData?.data.purpose.map(choice => ({
              key: choice.value,
              label: choice.label,
            })) || []
          }
        />
        <FormSelect
          className={styles.marginBottom}
          formik={formik}
          id='area'
          label={t(action_library_messages.properties.area)}
          options={
            areasData?.data?.areas.map(area => ({
              key: area.value,
              label: area.label,
            })) || []
          }
          required
        />
        <FormSelect
          className={styles.marginBottom}
          disabled={!values.area || availableSubareas.length === 0}
          formik={formik}
          id='subarea'
          label={t(action_library_messages.properties.subarea)}
          options={availableSubareas}
        />
        <FormSelect
          className={styles.marginBottom}
          formik={formik}
          id='assetTypes'
          label={t(action_library_messages.properties.asset_types)}
          multiple
          options={assetTypes || []}
        />
        <FormSelect
          className={styles.marginBottom}
          formik={formik}
          id='criticality'
          label={t(action_library_messages.properties.criticality)}
          options={sortedCriticalityOptions}
        />
        <div className={styles.marginBottom}>
          <FormTagsPicker formik={formik} id='referenceRegulation' label={t(action_library_messages.properties.reference)} options={[]} />
        </div>
        <FormTextInput
          className={styles.marginBottom}
          formik={formik}
          id='reportedTo'
          label={t(action_library_messages.properties.reported_to)}
        />
        <div className={styles.marginBottom}>
          <FormTagsPicker formik={formik} id='tags' label={t(action_library_messages.properties.tags)} options={[]} />
        </div>
        <Typography style={{ marginBottom: 12 }} variant='body2'>
          {t(action_library_messages.properties.validity_period)}
        </Typography>
        <div className={styles.inlineFields}>
          <div style={{ flex: 2 }}>
            <FormTextInput
              blockedChars={/[^0-9]/g}
              className={`${styles.marginBottom} ${styles.fullWidth}`}
              disabled={values.validityPeriodDoesNotApply || values.validityPeriod === '0'}
              formik={formik}
              id='validityPeriod'
              label={t(action_library_messages.properties.duration_in_months)}
            />
          </div>
          <div className={styles.marginBottom} style={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.validityPeriodDoesNotApply || false}
                  id='validityPeriodDoesNotApply'
                  name='validityPeriodDoesNotApply'
                  onChange={handleCheckboxChange}
                />
              }
              label={t(action_library_messages.properties.doesnt_apply)}
            />
          </div>
        </div>
      </CenteredGrid>
    </Paper>
  );
};

export default ActionTemplateProperties;
